// AddMeterModelPage.tsx - Enhanced version with API Configuration
import React, { useState, useEffect, useMemo } from "react";
import { ArrowLeft, Save, Loader2, Plus } from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";
import { FormField, FormService } from "@shared/services/FormServices";
import { DynamicForm } from "@shared/components/DynamicForm";
import {
  useMeterType,
  useCreateMeterModel,
  useManufacturerType,
  useMeterModelDetail,
  useCreateMeterManufacturer,
  useUpdateMeterModel,
} from "../hooks";
import { CreateMeterModelPayload } from "../types";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { useUtilityServices } from "@features/mx/readCycleManagement/hooks";
import { z } from 'zod';
import { logEvent } from "@shared/analytics/analytics";
import { Button } from "@shared/ui/button";

interface AddMeterModelPageProps {
  mode: "add" | "edit";
}

interface ComponentState {
  loading: boolean;
  validationErrors: Record<string, string>;
  initialDataLoading: boolean;
}

// Optimized field classes
const FIELD_CLASSES = {
  container: "w-full mb-4",
  label: "block text-sm font-semibold text-gray-700 mb-2",
  input:
    "w-full h-11 px-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all bg-gray-50",
  select:
    "w-full h-11 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all bg-gray-50",
  textarea:
    "w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all bg-gray-50",
  error: "text-red-600 text-sm mt-1",
  helperText: "text-xs text-gray-500 mt-1",
} as const;

const TECH_FIELD_CLASSES = {
  container: "w-full mb-2",
  label: "block text-sm font-semibold text-gray-700 mb-2",
  input:
    "w-full h-10 px-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-200 transition-all bg-gray-50",
  select:
    "w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all bg-gray-50",
  error: "text-red-600 text-sm mt-1",
  helperText: "text-xs text-gray-500 mt-1",
} as const;

// Common field options
const CONNECTION_SIZE_OPTIONS = [
  { label: "15mm", value: "15mm" },
  { label: "20mm", value: "20mm" },
  { label: "25mm", value: "25mm" },
  { label: "32mm", value: "32mm" },
  { label: "40mm", value: "40mm" },
  { label: "50mm", value: "50mm" },
];

const ACCURACY_OPTIONS = [
  { label: "Class A", value: "Class A (±2%)" },
  { label: "Class B", value: "Class B (±3%)" },
  { label: "Class C", value: "Class C (±5%)" },
  { label: "±1%", value: "±1%" },
  { label: "±2%", value: "±2%" },
  { label: "±5%", value: "±5%" },
];

const IP_RATING_OPTIONS = [
  { label: "IP54", value: "IP54" },
  { label: "IP65", value: "IP65" },
  { label: "IP67", value: "IP67" },
  { label: "IP68", value: "IP68" },
];

const COMMUNICATION_TYPE_OPTIONS = [
  { label: "LoRaWAN", value: "LORAWAN" },
  { label: "NB-IoT", value: "NB_IOT" },
  { label: "WiFi", value: "WIFI" },
  { label: "Cellular", value: "CELLULAR" },
  { label: "Zigbee", value: "ZIGBEE" },
  { label: "Bluetooth", value: "BLUETOOTH" },
];

const READ_TYPE_OPTIONS = [
  { label: "Analog", value: "analog" },
  { label: "Digital", value: "digital" },
  { label: "Dial", value: "dial" },
];

const DISPLAY_TYPE_OPTIONS = [
  { label: "LCD", value: "lcd" },
  { label: "LED", value: "led" },
  { label: "Mechanical", value: "mechanical" },
];

const FREQUENCY_OPTIONS = [
  { label: "Daily", value: "daily" },
  { label: "Weekly", value: "weekly" },
  { label: "Monthly", value: "monthly" },
  { label: "Quarterly", value: "quarterly" },
];

const PHOTO_READ_TYPE_OPTIONS = [
  { label: "OCR", value: "ocr" },
  { label: "Manual Entry", value: "manual entry" },
  { label: "Barcode/QR", value: "barcode qr" },
  { label: "Photo Input", value: "photo input" },
];

const PHOTO_DISPLAY_TYPE_OPTIONS = [
  ...DISPLAY_TYPE_OPTIONS,
  { label: "Analog", value: "analog" },
];

// API Configuration options
const AUTH_TYPE_OPTIONS = [
  { label: "API Key", value: "api_key" },
  { label: "OAuth 2.0", value: "oauth2" },
  { label: "Basic Auth", value: "basic_auth" },
  { label: "Bearer Token", value: "bearer_token" },
];

const DATA_FORMAT_OPTIONS = [
  { label: "JSON", value: "json" },
  { label: "XML", value: "xml" },
  { label: "CSV", value: "csv" },
  { label: "Plain Text", value: "text" },
];

export const AddMeterModelPage: React.FC<AddMeterModelPageProps> = ({
  mode,
}) => {
    const { data: utilities } = useUtilityServices();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const remoteUtilityId = getRemoteUtilityId();

  // Manufacturer inline input state
  const [showInlineManufacturerInput, setShowInlineManufacturerInput] =
    useState(false);
  const [newManufacturerName, setNewManufacturerName] = useState("");
  const [manufacturerInputError, setManufacturerInputError] = useState("");
  const [addingManufacturer, setAddingManufacturer] = useState(false);

  // Simplified state management
  const [state, setState] = useState<ComponentState>({
    loading: false,
    validationErrors: {},
    initialDataLoading: mode === "edit",
  });

  const updateState = (updates: Partial<ComponentState>) => {
    setState((prev) => ({ ...prev, ...updates }));
  };

  // API hooks
  const { data: meterTypesData, isLoading: meterTypesLoading } = useMeterType();
  const createMeterModelMutation = useCreateMeterModel();
  const createManufacturerMutation = useCreateMeterManufacturer();
  const updateMeterModelMutation = useUpdateMeterModel();

  const { data: existingMeterData, isLoading: existingDataLoading } =
    useMeterModelDetail({
      remote_utility_id: remoteUtilityId,
      meter_model_id: mode === "edit" && id ? id : "",
    });

  const {
    data: manufacturerOptions,
    isLoading: manufacturersLoading,
    refetch: refetchManufacturers,
  } = useManufacturerType({
    remote_utility_id: remoteUtilityId,
    is_dropdown: 1,
  });

  // Memoized options
  const meterTypeOptions = useMemo(
    () =>
      meterTypesData?.result?.map((item: any) => ({
        label: item.value,
        value: item.value,
      })) || [],
    [meterTypesData]
  );

  const manufacturerTypeOptions = useMemo(
    () =>
      manufacturerOptions?.result?.map((item: any) => ({
        label: item.name,
        value: item.id,
      })) || [],
    [manufacturerOptions]
  );

  const meterTypeMap = useMemo(() => {
    const map: Record<string, number> = {};
    meterTypesData?.result?.forEach((type: any) => {
      map[type.value] = type.key;
    });
    return map;
  }, [meterTypesData]);

  // Form configuration
  const form = FormService.createForm({
    fields: [],
    mode: "onChange",
    resetOnSubmit: false,
    showResetButton: false,
    showSubmitButton: false,
  });

    const getUtilityOptions = utilities
    ? utilities
        .filter((utility) => utility.isActive === true)
        .map((utility) => ({
          value: utility.name,
          label: utility.name,
        }))
    : [];
  // Manufacturer inline input handlers
  const handleAddManufacturer = async () => {
    if (!newManufacturerName.trim()) {
      setManufacturerInputError("Manufacturer name is required");
      return;
    }

    setAddingManufacturer(true);
    setManufacturerInputError("");

    try {
      const newManufacturer = await createManufacturerMutation.mutateAsync({
        remote_utility_id: Number(remoteUtilityId),
        name: newManufacturerName.trim(),
        country: "",
        website_url: "",
        contact_number: "",
        description: "",
      });

      await refetchManufacturers();

      const manufacturerId = newManufacturer?.id;
      form.setValue("manufacturer", manufacturerId);

      setNewManufacturerName("");
      setShowInlineManufacturerInput(false);
    } catch (error: any) {
      setManufacturerInputError(
        error.message || "Failed to create manufacturer"
      );
    } finally {
      setAddingManufacturer(false);
    }
  };

  const handleCancelInlineManufacturer = () => {
    setNewManufacturerName("");
    setManufacturerInputError("");
    setShowInlineManufacturerInput(false);
  };

  // Custom manufacturer field render
  const renderManufacturerField = (field: FormField, form: any, error: any) => (
    <div className="w-full">
      {!showInlineManufacturerInput ? (
        <div className="flex items-start gap-2">
          <div className="flex-1">
            <select
              {...form.register(field.name)}
              className={`${field.classes?.select} ${
                error ? "border-red-500" : ""
              }`}
              disabled={field.disabled}
            >
              <option value="">
                {field.placeholder || "Select manufacturer"}
              </option>
              {field.options?.map((option, index) => (
                <option
                  key={`${field.name}-${String(option.value)}-${index}`}
                  value={String(option.value)}
                  disabled={option.disabled}
                  title={option.description}
                >
                  {option.label}
                </option>
              ))}
            </select>
            {error && (
              <span className={FIELD_CLASSES.error} role="alert">
                {error}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={() => setShowInlineManufacturerInput(true)}
            disabled={manufacturersLoading || addingManufacturer}
            className="h-11 w-11 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors flex items-center justify-center"
            title="Add new manufacturer"
          >
            <Plus className="w-5 h-5 text-gray-600" />
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <input
              type="text"
              value={newManufacturerName}
              onChange={(e) => setNewManufacturerName(e.target.value)}
              placeholder="Enter new manufacturer"
              className="w-full h-11 px-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all bg-gray-50"
              disabled={addingManufacturer}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") handleAddManufacturer();
                else if (e.key === "Escape") handleCancelInlineManufacturer();
              }}
            />
            {manufacturerInputError && (
              <p className={FIELD_CLASSES.error}>{manufacturerInputError}</p>
            )}
          </div>
          <button
            type="button"
            onClick={handleAddManufacturer}
            disabled={addingManufacturer || !newManufacturerName.trim()}
            className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 flex items-center gap-2 transition-colors h-11"
          >
            {addingManufacturer && <Loader2 className="w-4 h-4 animate-spin" />}
            Add
          </button>
          <button
            type="button"
            onClick={handleCancelInlineManufacturer}
            disabled={addingManufacturer}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 transition-colors h-11"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );

  // Custom checkbox group render
  const renderCheckboxGroup = (field: FormField, form: any, error: any) => {
    const currentValue = form.watch(field.name) || [];

    const handleCheckboxChange = (value: string, checked: boolean) => {
      let newValues = [...currentValue];
      if (checked) {
        if (!newValues.includes(value)) newValues.push(value);
      } else {
        newValues = newValues.filter((v) => v !== value);
      }
      form.setValue(field.name, newValues);
    };

    return (
      <div className="flex gap-6">
        {field.options?.map((option) => (
          <label
            key={String(option.value)}
            className="flex items-center gap-2 cursor-pointer"
          >
            <input
              type="checkbox"
              checked={currentValue.includes(option.value)}
              onChange={(e) =>
                handleCheckboxChange(option.value as string, e.target.checked)
              }
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
            />
            <span className="text-sm font-medium text-gray-700">
              {option.label}
            </span>
          </label>
        ))}
      </div>
    );
  };
  // Form field definitions
  const generalInfoFields: FormField[] = [
    {
      name: "manufacturer",
      label: "Manufacturer",
      type: "select",
       helperText: "Choose the company that manufactures this meter model",

      required: true,
      placeholder: manufacturersLoading
        ? "Loading manufacturers..."
        : "Select manufacturer",
      options: manufacturerTypeOptions,
      disabled: manufacturersLoading,
      customValidation: (value) =>
        value ? true : "Please select a manufacturer",
      classes: FIELD_CLASSES,
      customRender: renderManufacturerField,
    },
    {
      name: "modelName",
      label: "Model Name",
      type: "text",
            helperText: "The official product name or model number as specified by the manufacturer",

      required: true,
      placeholder: "Enter model name",
      maxLength: 100,
      customValidation: (value: string) => {
        if (!value) return "Model name is required";
        if (value.trim().length < 2)
          return "Model name must be at least 2 characters";
        if (value.length > 100)
          return "Model name cannot exceed 100 characters";
        return true;
      },
      classes: FIELD_CLASSES,
    },
    {
      name: "meterType",
      label: "Meter Type",
      type: "select",
      required: true,
            helperText: "The category of meter based on its primary function (e.g., Water, Gas, Electric)",

      placeholder: meterTypesLoading
        ? "Loading meter types..."
        : "Select meter type",
      options: meterTypeOptions,
      disabled: meterTypesLoading,
      customValidation: (value) =>
        value ? true : "Please select a meter type",
      classes: FIELD_CLASSES,
    },
    {
      name: "meterCode",
      label: "Make Code",
      type: "text",
            helperText: "Internal reference code for the meter model",

      required: true,
      placeholder: "Enter make code (e.g., KC603)",
      customValidation: (value: string) => {
        if (!value) return "Make code is required";
        if (value.trim().length < 2)
          return "Make code must be at least 2 characters";
        return true;
      },
      classes: FIELD_CLASSES,
    },
    {
      name: "description",
      label: "Description",
      type: "textarea",
      required: true,
            helperText: "Detailed description of the meter model",

      placeholder: "Enter meter model description",
      rows: 3,
      classes: {
        ...FIELD_CLASSES,
        container: "w-full mb-4 col-span-2",
      },
    },
  {
      name: "utilityService",
      label: "Utility Services",
      type: "select",
      required: true, 
      showHelperTooltip: true,
            helperText: "Select the utility services this meter model supports",

      tooltipText:
        "Select which utility this route will serve. Each route can only track one type of utility.",
      tooltipPosition: "right" as const,
      options: getUtilityOptions,

      placeholder: "Select utility services...",
      fullWidth: true,
      searchable: true,
      clearable: true,
      maxDisplayItems: 3,
      closeOnSelect: false,
      className: "col-span-2",
      classes: FIELD_CLASSES,
      
    },
  ];

  const technicalSpecsFields: FormField[] = [
    {
      name: "dialLength",
      label: "Dial Length (mm)",
      type: "number",
      required: false,
      min: 1,
      max: 1000,
      placeholder: "0",
      defaultValue: 0,
      classes: TECH_FIELD_CLASSES,
      helperText: "The length of the meter dial display in millimeters",

    },
    {
      name: "dialCount",
      label: "Dial Count",
      type: "number",
      required: false,
            helperText: "Number of individual dials or digits on the meter display",

      min: 1,
      max: 20,
      placeholder: "0",
      defaultValue: 0,
      classes: TECH_FIELD_CLASSES,
    },
    {
      name: "maxFlow",
      label: "Max Flow (m³/h)",
      type: "decimal",
      required: false,
      placeholder: "0",
      defaultValue: 0,
      classes: TECH_FIELD_CLASSES,
                  helperText: "Maximum flow rate the meter can accurately measure in cubic meters per hour",

    },
    {
      name: "connectionSize",
      label: "Connection Size",
      type: "select",
      required: false,
      placeholder: "Select size",
      defaultValue: "",
      helperText: "Standard pipe connection size for meter installation",

      options: CONNECTION_SIZE_OPTIONS,
      classes: TECH_FIELD_CLASSES,
    },
    {
      name: "weight",
      label: "Weight (kg)",
      type: "decimal",
      required: false,
      min: 0,
      placeholder: "0",
      defaultValue: 0,
            helperText: "Total weight of the meter unit in kilograms",

      classes: TECH_FIELD_CLASSES,
    },
    {
      name: "accuracy",
      label: "Accuracy",
      type: "select",
      required: false,
      placeholder: "Select accuracy",
      defaultValue: "",
      options: ACCURACY_OPTIONS,
      classes: TECH_FIELD_CLASSES,
            helperText: "Measurement accuracy rating or tolerance level of the meter",

    },
    {
      name: "length",
      label: "Length (mm)",
      type: "number",
      required: false,
      min: 1,
      placeholder: "0",
      defaultValue: 0,
      classes: TECH_FIELD_CLASSES,
            helperText: "Overall length dimension of the meter body in millimeters",

    },
    {
      name: "width",
      label: "Width (mm)",
      type: "number",
      required: false,
      min: 1,
      placeholder: "0",
      defaultValue: 0,
      classes: TECH_FIELD_CLASSES,
            helperText: "Overall width dimension of the meter body in millimeters",

    },
    {
      name: "height",
      label: "Height (mm)",
      type: "number",
      required: false,
      min: 1,
      placeholder: "0",
      defaultValue: 0,
      classes: TECH_FIELD_CLASSES,
            helperText: "Overall height dimension of the meter body in millimeters",

    },
    {
      name: "material",
      label: "Material",
      type: "text",
      required: false,
      placeholder: "e.g. Brass body, polymer register",
            helperText: "Main construction materials",

      defaultValue: "",
      classes: TECH_FIELD_CLASSES,
    },
    {
      name: "ipRating",
      label: "IP Rating",
      type: "select",
      required: false,
      placeholder: "Select IP rating",
      defaultValue: "",
      options: IP_RATING_OPTIONS,
      classes: TECH_FIELD_CLASSES,
            helperText: "Ingress Protection rating indicating resistance to dust and water penetration",

    },
  ];


  const smartMeterFields: FormField[] = [
    {
      name: "communicationType",
      label: "Communication Type",
      type: "select",
      required: false,
      placeholder: "Select communication type",
      defaultValue: "",
      options: COMMUNICATION_TYPE_OPTIONS,
      helperText: "Method used for remote communication (e.g., LoRaWAN, NB-IoT, WiFi)",
      classes: TECH_FIELD_CLASSES,
    },
    {
      name: "firmwareVersion",
      label: "Firmware Version",
      type: "number",
      required: false,
      placeholder: "e.g. 1",
      helperText: "Current firmware version number",
      defaultValue: 1,
      classes: TECH_FIELD_CLASSES,
    },
    {
      name: "batteryLife",
      label: "Battery Life (Years)",
      type: "number",
      required: false,
      min: 1,
      max: 30,
      placeholder: "0",
      defaultValue: 5,
      helperText: "Expected battery life in years under normal operating conditions",
      classes: TECH_FIELD_CLASSES,
    },
    {
      name: "supportsOtaUpdates",
      label: "Supports OTA Firmware Updates",
      type: "switch",
      required: false,
      defaultValue: false,
      helperText: "Can receive firmware updates over the air",
      classes: TECH_FIELD_CLASSES,
    },
    {
      name: "encryptionEnabled",
      label: "Encryption Enabled",
      type: "switch",
      required: false,
      defaultValue: true,
      helperText: "Data transmission is encrypted",
      classes: TECH_FIELD_CLASSES,
    },
    {
      name: "enableApiIntegration",
      label: "Enable API Integration",
      type: "switch",
      required: false,
      defaultValue: false,
      helperText: "Allow third-party API access",
      classes: TECH_FIELD_CLASSES,
    },
  ];

  const apiConfigurationFields: FormField[] = [
    {
      name: "apiEndpoints",
      label: "API Endpoints",
      type: "text",
      required: true,
      placeholder: "e.g., bynry.com/api/v1",
      helperText: "Base URL for API endpoints",
      customValidation: (value: string) => {
        if (!value)
          return "API endpoint is required when API integration is enabled";
        try {
          new URL(value.startsWith("http") ? value : `https://${value}`);
          return true;
        } catch {
          return "Please enter a valid URL";
        }
      },
      classes: TECH_FIELD_CLASSES,
    },
    {
      name: "authType",
      label: "Authentication Type",
      type: "select",
      required: true,
      placeholder: "Select authentication type",
      defaultValue: "api_key",
      options: AUTH_TYPE_OPTIONS,
      helperText: "Method used to authenticate API requests (API Key, OAuth, etc.)",
      classes: TECH_FIELD_CLASSES,
    },
    {
      name: "dataFormat",
      label: "Data Format",
      type: "select",
      required: true,
      placeholder: "Select data format",
      defaultValue: "json",
      options: DATA_FORMAT_OPTIONS,
      helperText: "Format used for data exchange between meter and system",
      classes: TECH_FIELD_CLASSES,
    },
    {
      name: "readNowSupported",
      label: "Read Now Command",
      type: "switch",
      required: false,
      defaultValue: true,
      helperText: "Supports immediate meter reading command",
      classes: TECH_FIELD_CLASSES,
    },
    {
      name: "pushIntervalDataSupported",
      label: "Push Interval Data",
      type: "switch",
      required: false,
      defaultValue: false,
      helperText: "Supports automatic data push at intervals",
      classes: TECH_FIELD_CLASSES,
    },
    {
      name: "diagnosticsSupported",
      label: "Diagnostics Command",
      type: "switch",
      required: false,
      defaultValue: true,
      helperText: "Supports diagnostic commands",
      classes: TECH_FIELD_CLASSES,
    },
    {
      name: "otaUpdateSupported",
      label: "OTA Update Command",
      type: "switch",
      required: false,
      defaultValue: false,
      helperText: "Supports over-the-air update commands",
      classes: TECH_FIELD_CLASSES,
    },
  ];

  const manualMeterFields: FormField[] = [
    {
      name: "readType",
      label: "Read Type",
      type: "select",
      required: false,
      placeholder: "Select read type",
      defaultValue: "",
      options: READ_TYPE_OPTIONS,
      helperText: "Method used to read meter values (Visual, Touch, etc.)",
      classes: TECH_FIELD_CLASSES,
    },
    {
      name: "displayType",
      label: "Display Type",
      type: "select",
      required: false,
      placeholder: "Select display type",
      defaultValue: "",
      options: DISPLAY_TYPE_OPTIONS,
      helperText: "Type of display used to show meter readings (Analog, Digital, LCD)",
      classes: TECH_FIELD_CLASSES,
    },
    {
      name: "recommendedReadFrequency",
      label: "Recommended Read Frequency",
      type: "select",
      required: false,
      placeholder: "Select frequency",
      defaultValue: "",
      options: FREQUENCY_OPTIONS,
      helperText: "Recommended interval for taking manual readings",
      classes: TECH_FIELD_CLASSES,
    },
    {
      name: "tamperProofManual",
      label: "Tamper-Proof",
      type: "switch",
      required: false,
      defaultValue: false,
      helperText: "Meter has tamper-proof features",
      classes: TECH_FIELD_CLASSES,
    },
  ];

  const photoMeterFields: FormField[] = [
    {
      name: "readTypePhoto",
      label: "Read Type",
      type: "select",
      required: false,
      placeholder: "Select read type",
      defaultValue: "",
      options: PHOTO_READ_TYPE_OPTIONS,
      helperText: "Method used for photo-based meter reading (OCR, Manual entry)",
      classes: TECH_FIELD_CLASSES,
    },
    {
      name: "displayTypePhoto",
      label: "Display Type",
      type: "select",
      required: false,
      placeholder: "Select display type",
      defaultValue: "",
      options: PHOTO_DISPLAY_TYPE_OPTIONS,
      helperText: "Type of display that will be photographed for readings",
      classes: TECH_FIELD_CLASSES,
    },
    {
      name: "recommendedReadFrequencyPhoto",
      label: "Recommended Read Frequency",
      type: "select",
      required: false,
      placeholder: "Select frequency",
      defaultValue: "",
      options: FREQUENCY_OPTIONS,
      helperText: "Recommended interval for taking photo-based readings",
      classes: TECH_FIELD_CLASSES,
    },
    {
      name: "tamperProofPhoto",
      label: "Tamper-Proof",
      type: "switch",
      required: false,
      defaultValue: false,
      helperText: "Meter has tamper-proof features",
      classes: TECH_FIELD_CLASSES,
    },
  ];

  // Load existing data in edit mode
  useEffect(() => {
    if (mode === "edit" && existingMeterData && !existingDataLoading) {
      updateState({ initialDataLoading: true });

      try {
        const result = existingMeterData.result;
        console.log(result);
        const formData = {
          manufacturer: result.meterManufacturer?.id,
          modelName: result?.meterModel,
          meterType: result?.meterTypeDisplay,
          meterCode: result?.meterMake,
          utilityService: result?.utilityService,
          description: result?.description,

          // Technical specifications
          dialLength: result?.technicalSpecifications?.dialLength || 0,
          dialCount: result?.technicalSpecifications?.dialCount || 0,
          maxFlow: result?.technicalSpecifications?.maxFlow || 0,
          connectionSize: result?.technicalSpecifications?.connectionSize || "",
          weight: result?.technicalSpecifications?.weight || 0,
          accuracy: result?.technicalSpecifications?.accuracy || "",
          length: result?.technicalSpecifications?.length || 0,
          width: result?.technicalSpecifications?.width || 0,
          height: result?.technicalSpecifications?.height || 0,
          material: result?.technicalSpecifications?.material || "",
          ipRating: result?.technicalSpecifications?.ipRating || "",

          // Smart meter configuration
          communicationType:
            result?.technicalSpecifications?.meterConfiguration
              ?.communicationType || "",
          firmwareVersion:
            result?.technicalSpecifications?.meterConfiguration
              ?.firmwareVersion || 1,
          batteryLife:
            result?.technicalSpecifications?.meterConfiguration?.batteryLife ||
            5,
          supportsOtaUpdates:
            result?.technicalSpecifications?.meterConfiguration
              ?.otaFirmwareUpdate || false,
          encryptionEnabled:
            result?.technicalSpecifications?.meterConfiguration
              ?.encryptionEnabled || false,
          enableApiIntegration:
            result?.technicalSpecifications?.meterConfiguration
              ?.enableApiIntegration || false,

          // API Configuration fields
          apiEndpoints:
            result?.technicalSpecifications?.apiConfiguration?.apiEndpoints ||
            "",
          authType:
            result?.technicalSpecifications?.apiConfiguration?.authType ||
            "api_key",
          dataFormat:
            result?.technicalSpecifications?.apiConfiguration?.dataFormat ||
            "json",
          readNowSupported:
            result?.technicalSpecifications?.apiConfiguration?.supportedCommands
              ?.readNow ?? true,
          pushIntervalDataSupported:
            result?.technicalSpecifications?.apiConfiguration?.supportedCommands
              ?.pushIntervalData ?? false,
          diagnosticsSupported:
            result?.technicalSpecifications?.apiConfiguration?.supportedCommands
              ?.diagnostics ?? true,
          otaUpdateSupported:
            result?.technicalSpecifications?.apiConfiguration?.supportedCommands
              ?.otaUpdate ?? false,

          // Manual meter configuration
          readType:
            result?.technicalSpecifications?.meterConfiguration?.readType || "",
          displayType:
            result?.technicalSpecifications?.meterConfiguration?.displayType ||
            "",
          recommendedReadFrequency:
            result?.technicalSpecifications?.meterConfiguration
              ?.readFrequency || "",
          tamperProofManual:
            result?.technicalSpecifications?.meterConfiguration?.tamperProof ||
            false,

          // Photo meter configuration
          readTypePhoto:
            result?.technicalSpecifications?.meterConfiguration?.readType || "",
          displayTypePhoto:
            result?.technicalSpecifications?.meterConfiguration?.displayType ||
            "",
          recommendedReadFrequencyPhoto:
            result?.technicalSpecifications?.meterConfiguration
              ?.readFrequency || "",
          tamperProofPhoto:
            result?.technicalSpecifications?.meterConfiguration?.tamperProof ||
            false,
        };

        Object.entries(formData).forEach(([key, value]) => {
          form.setValue(key, value);
        });
      } finally {
        updateState({ initialDataLoading: false });
      }
    }
  }, [existingMeterData, existingDataLoading, mode, form]);

  
  // Navigation handlers
  const handleBack = () => navigate("/mx/manufacturer");
  const handleCancel = () => {
    form.reset();
    handleBack();
  };

  // Transform form data to API payload
const transformFormDataToPayload = (data: any): CreateMeterModelPayload => {
  const meterType = form.watch("meterType");
  console.log("Transform - Selected meter type:", meterType);
  console.log("Transform - Meter type map:", meterTypeMap);
  
  // Get the correct meter type key
  const meterTypeKey = meterTypeMap[meterType];
  console.log("Transform - Meter type key for", meterType, ":", meterTypeKey);
  
  const utilityService = data.utilityService;

  const technicalSpecs = {
    dial_length: Number(data.dialLength) || 0,
    dial_count: Number(data.dialCount) || 0,
    max_flow: Number(data.maxFlow) || 0,
    connection_size: data.connectionSize || "",
    weight: Number(data.weight) || 0,
    accuracy: data.accuracy || "",
    length: Number(data.length) || 0,
    width: Number(data.width) || 0,
    height: Number(data.height) || 0,
    material: data.material || "",
    ip_rating: data.ipRating || "",
    meter_configuration: {} as any,
    api_configuration: {} as any,
  };

  // Fix the conditional logic here
  console.log("Checking meter type conditions for:", meterType);
  
  if (meterType === "Smart Meter") {
    console.log("Setting Smart Meter configuration");
    technicalSpecs.meter_configuration = {
      communication_type: data.communicationType || "",
      firmware_version: Number(data.firmwareVersion) || 1,
      battery_life: Number(data.batteryLife) || 5,
      ota_firmware_update: Boolean(data.supportsOtaUpdates),
      encryption_enabled: Boolean(data.encryptionEnabled),
      enable_api_integration: Boolean(data.enableApiIntegration),
    };

    if (data.enableApiIntegration) {
      technicalSpecs.api_configuration = {
        api_endpoints: data.apiEndpoints || "",
        auth_type: data.authType || "api_key",
        data_format: data.dataFormat || "json",
        supported_commands: {
          read_now: Boolean(data.readNowSupported),
          push_iterval_data: Boolean(data.pushIntervalDataSupported),
          dignostics: Boolean(data.diagnosticsSupported),
          ota_update: Boolean(data.otaUpdateSupported),
        },
      };
    }
  } else if (meterType === "Manual Meter") {
    console.log("Setting Manual Meter configuration");
    technicalSpecs.meter_configuration = {
      read_type: data.readType || "",
      display_type: data.displayType || "",
      read_frequency: data.recommendedReadFrequency || "",
      tamper_proof: Boolean(data.tamperProofManual),
    };
  } else if (meterType === "Photo Meter") {
    console.log("Setting Photo Meter configuration");
    technicalSpecs.meter_configuration = {
      read_type: data.readTypePhoto || "",
      display_type: data.displayTypePhoto || "",
      read_frequency: data.recommendedReadFrequencyPhoto || "",
      tamper_proof: Boolean(data.tamperProofPhoto),
    };
  }

  const payload = {
    remote_utility_id: Number(remoteUtilityId),
    meter_manufacturer: Number(data.manufacturer),
    meter_make: data.meterCode || "",
    meter_model: data.modelName || "",
    meter_type: meterTypeKey || 0, // Use the correct key from meterTypeMap
    description: data.description || "",
    utility_service: utilityService,
    technical_specifications: technicalSpecs,
  };

  console.log("Final payload:", payload);
  return payload;
};

  const handleSubmit = async (data: any) => {
    updateState({ loading: true });

    try {
      
      const formData = form.getValues();
      const payload = transformFormDataToPayload(formData);

      if (mode === "edit" && id) {
        await updateMeterModelMutation.mutateAsync({ id, payload }, {
          onSuccess: () => {
            logEvent("MX Model Specification Update", {
              ...payload,
            })
          }
        });
        navigate(`/mx/manufacturer`);
      } else {
        await createMeterModelMutation.mutateAsync(payload, {
          onSuccess: () => {
            logEvent("MX Model Created", {
              ...payload
            })
          }
        });
        form.reset();
        navigate("/mx/manufacturer");
      }
    } catch (error: any) {
      console.error("Error submitting form:", error);
    } finally {
      updateState({ loading: false });
    }
  };

// Watch meter type and API integration toggle
const meterType = form.watch("meterType");
const apiIntegrationEnabled = form.watch("enableApiIntegration");

// Debug: Add console log to see what value is actually selected
console.log("Selected meter type:", meterType);

// Use case-insensitive and flexible matching
const showSmartConfig = meterType?.toLowerCase().includes("smart");
const showManualConfig = meterType?.toLowerCase().includes("manual");
const showPhotoConfig = meterType?.toLowerCase().includes("photo");
const showApiConfig = showSmartConfig && apiIntegrationEnabled;

  // Show loading spinner for initial data in edit mode
  if (state.initialDataLoading || (mode === "edit" && existingDataLoading)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-gray-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading meter data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={handleCancel}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="page-title">
                  {mode === "edit" ? "Edit Meter Model" : "Add New Meter Model"}
                </h1>
                <p className="page-subtitle">
                  {mode === "edit"
                    ? "Update meter model configuration"
                    : "Create a new meter model configuration"}
                </p>
              </div>
            </div>
            {/* <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleCancel}
                disabled={state.loading}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
              <Button
                type="submit"
                form="meter-model-form"
                disabled={state.loading}
                className="px-6 py-2 disabled:opacity-50 flex items-center gap-2 transition-colors"
              >
                {state.loading && <Loader2 className="w-4 h-4 animate-spin" />}
                <Save className="w-4 h-4" />
                {state.loading
                  ? mode === "edit"
                    ? "Updating..."
                    : "Saving..."
                  : mode === "edit"
                  ? "Update Meter Model"
                  : "Save Meter Model"}
              </Button>
            </div> */}
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <form
          id="meter-model-form"
          onSubmit={form.handleSubmit(handleSubmit)}
          className="space-y-8"
        >
          {/* General Information */}
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                General Information
              </h3>
            </div>
            <div className="p-6">
              <DynamicForm
                fields={generalInfoFields}
                form={form}
                onSubmit={() => {}}
                loading={state.loading}
                config={{
                  layout: "vertical",
                  spacing: "comfortable",
                  showProgress: false,
                  showResetButton: false,
                  showSubmitButton: false,
                }}
                gridColumns={2}
                responsiveColumns={{ sm: 1, md: 2, lg: 2, xl: 2 }}
              />
            </div>
          </div>

          {/* Technical Specifications */}
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Technical Specifications
              </h3>
            </div>
            <div className="p-6">
              <DynamicForm
                fields={technicalSpecsFields}
                form={form}
                onSubmit={() => {}}
                loading={state.loading}
                config={{
                  layout: "vertical",
                  spacing: "comfortable",
                  showProgress: false,
                  showResetButton: false,
                  showSubmitButton: false,
                }}
                gridColumns={3}
                responsiveColumns={{ sm: 1, md: 2, lg: 3, xl: 3 }}
              />
            </div>
          </div>

          {/* Smart Meter Configuration */}
          {showSmartConfig && (
            <div className="bg-white rounded-lg border border-blue-200 bg-blue-50">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  Smart Meter Configuration
                </h3>
              </div>
              <div className="p-6">
                <div className="mb-4 p-3 bg-blue-100 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <span className="font-medium">Smart Meter Detected:</span>{" "}
                    Configure additional settings for smart meter functionality.
                  </p>
                </div>
                <DynamicForm
                  fields={smartMeterFields}
                  form={form}
                  onSubmit={() => {}}
                  loading={state.loading}
                  config={{
                    layout: "vertical",
                    spacing: "comfortable",
                    showProgress: false,
                    showResetButton: false,
                    showSubmitButton: false,
                  }}
                  gridColumns={2}
                  responsiveColumns={{ sm: 1, md: 2, lg: 2, xl: 2 }}
                />
              </div>
            </div>
          )}

          {/* API Configuration */}
          {showApiConfig && (
            <div className="bg-white rounded-lg border border-purple-200 bg-purple-50">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  API Configuration
                </h3>
              </div>
              <div className="p-6">
                <div className="mb-4 p-3 bg-purple-100 border border-purple-200 rounded-lg">
                  <p className="text-sm text-purple-800">
                    <span className="font-medium">
                      API Integration Enabled:
                    </span>{" "}
                    Configure API endpoints and supported commands for remote
                    meter access.
                  </p>
                </div>
                <DynamicForm
                  fields={apiConfigurationFields}
                  form={form}
                  onSubmit={() => {}}
                  loading={state.loading}
                  config={{
                    layout: "vertical",
                    spacing: "comfortable",
                    showProgress: false,
                    showResetButton: false,
                    showSubmitButton: false,
                  }}
                  gridColumns={2}
                  responsiveColumns={{ sm: 1, md: 2, lg: 2, xl: 2 }}
                />
              </div>
            </div>
          )}

          {/* Manual Meter Configuration */}
          {showManualConfig && (
            <div className="bg-white rounded-lg border border-gray-200 bg-gray-50">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  Manual Meter Configuration
                </h3>
              </div>
              <div className="p-6">
                <div className="mb-4 p-3 bg-gray-100 border border-gray-200 rounded-lg">
                  <p className="text-sm text-gray-800">
                    <span className="font-medium">Manual Meter Detected:</span>{" "}
                    Configure settings for manual meter reading.
                  </p>
                </div>
                <DynamicForm
                  fields={manualMeterFields}
                  form={form}
                  onSubmit={() => {}}
                  loading={state.loading}
                  config={{
                    layout: "vertical",
                    spacing: "comfortable",
                    showProgress: false,
                    showResetButton: false,
                    showSubmitButton: false,
                  }}
                  gridColumns={2}
                  responsiveColumns={{ sm: 1, md: 2, lg: 2, xl: 2 }}
                />
              </div>
            </div>
          )}

          {/* Photo Meter Configuration */}
          {showPhotoConfig && (
            <div className="bg-white rounded-lg border border-green-200 bg-green-50">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  Photo Meter Configuration
                </h3>
              </div>
              <div className="p-6">
                <div className="mb-4 p-3 bg-green-100 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-800">
                    <span className="font-medium">Photo Meter Detected:</span>{" "}
                    Configure settings for photo-based meter reading.
                  </p>
                </div>
                <DynamicForm
                  fields={photoMeterFields}
                  form={form}
                  onSubmit={() => {}}
                  loading={state.loading}
                  config={{
                    layout: "vertical",
                    spacing: "comfortable",
                    showProgress: false,
                    showResetButton: false,
                    showSubmitButton: false,
                  }}
                  gridColumns={2}
                  responsiveColumns={{ sm: 1, md: 2, lg: 2, xl: 2 }}
                />
              </div>
            </div>
          )}
        </form>
        <div className="flex justify-end mt-4 items-center gap-3">
              <button
                type="button"
                onClick={handleCancel}
                disabled={state.loading}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
              <Button
                type="submit"
                form="meter-model-form"
                disabled={state.loading}
                className="px-6 py-2 disabled:opacity-50 flex items-center gap-2 transition-colors"
              >
                {state.loading && <Loader2 className="w-4 h-4 animate-spin" />}
                <Save className="w-4 h-4" />
                {state.loading
                  ? mode === "edit"
                    ? "Updating..."
                    : "Saving..."
                  : mode === "edit"
                  ? "Update Meter Model"
                  : "Save Meter Model"}
              </Button>
            </div>
      </div>
    </div>
  );
};
